import { types, flow, getRoot, getEnv } from 'mobx-state-tree';
import { generateMSTModelFromSchema, createModelCollection } from './schemaToMST.js';

/**
 * MST модель для управления динамическими моделями
 * Загружает JSON схемы с сервера и генерирует MST модели на лету
 */
const MSTModelStore = types
    .model('MSTModelStore', {
        // Состояние схем моделей - frozen для JSON данных
        schemas: types.frozen({}),
        isLoading: types.optional(types.boolean, false),
        error: types.maybeNull(types.string),
        
        // Текущий роут для управления подписками
        currentRoute: types.maybeNull(types.string),
    })
    .volatile(() => ({
        // Сгенерированные MST модели (не сериализуемые)
        mstModels: {},

        // Менеджеры коллекций для каждой сущности
        collectionManagers: {},

        // Метаданные коллекций для ленивого создания
        collectionMetadata: new Map(),

        // Активные подписки WebSocket
        activeSubscriptions: new Set(),

        // Кеш сторов страниц
        pageStores: new Map(),

        // Локальные коллекции текущей страницы (для изоляции данных)
        pageCollections: new Map(),
    }))
    .views(self => ({
        // Получить схему модели
        getSchema(modelName) {
            return self.schemas[modelName];
        },
        
        // Получить MST модель
        getMSTModel(modelName) {
            return self.mstModels[modelName];
        },
        
        // Получить CollectionManager для сущности
        getCollectionManager(entityName) {
            // Предотвращаем создание коллекции editor - она должна быть только локальной в UniversalEditor
            if (entityName === 'editor') {
                console.warn('[MSTModelStore] Попытка создать коллекцию editor заблокирована - editor должен быть только локальным экземпляром');
                return null;
            }
            // Ленивое создание при первом обращении
            return self.getOrCreateCollectionManager(entityName);
        },
        
        // Получить все схемы моделей
        getAllSchemas() {
            return self.schemas;
        },
        
        // Совместимость со старым API
        getAllModels() {
            return self.schemas;
        },
        
        getModel(modelName) {
            return self.schemas[modelName];
        },
        
        // Получить API endpoint для текущего или указанного роута
        getApiEndpoint(pathname = null) {
            const targetPath = pathname || self.currentRoute;
            const routesModel = self.schemas.routes;
            const routeConfig = self.findRouteConfig(targetPath, routesModel?.routes);
            return routeConfig?.api;
        },
        
        // Найти конфигурацию роута рекурсивно
        findRouteConfig(pathname, routes, basePath = '') {
            if (!routes) return null;
            
            for (const [path, config] of Object.entries(routes)) {
                // Пропускаем специальные паттерны
                if (path === '*') continue;
                
                const fullPath = basePath + path;
                
                // Точное совпадение
                if (fullPath === pathname) {
                    console.log('[MSTModelStore] Found route config for', pathname, ':', config);
                    return config;
                }
                
                // Проверяем дочерние роуты
                if (config.children) {
                    const childConfig = self.findRouteConfig(pathname, config.children, fullPath);
                    if (childConfig) return childConfig;
                }
                
                // Проверяем динамические роуты (/:id)
                if (path.includes(':')) {
                    const regex = new RegExp('^' + fullPath.replace(/:[^/]+/g, '[^/]+') + '$');
                    if (regex.test(pathname)) {
                        return config;
                    }
                }
            }
            return null;
        },
        
        // Определить коллекции для роута из routes модели
        getCollectionsForRoute(pathname) {
            const collections = new Set();
            
            // Получаем модель роутов
            const routesModel = self.schemas.routes;
            if (!routesModel || !routesModel.routes) {
                console.warn('[MSTModelStore] Routes model not loaded');
                return collections;
            }
            
            // Сначала добавляем глобальные подписки (паттерн *)
            if (routesModel.routes['*'] && routesModel.routes['*'].subscriptions) {
                console.log('[MSTModelStore] Adding global subscriptions:', routesModel.routes['*'].subscriptions);
                routesModel.routes['*'].subscriptions.forEach(sub => collections.add(sub));
            }
            
            // Затем добавляем подписки конкретного роута
            const routeConfig = self.findRouteConfig(pathname, routesModel.routes);
            if (routeConfig && routeConfig.subscriptions) {
                routeConfig.subscriptions.forEach(sub => collections.add(sub));
            }
            
            console.log('[MSTModelStore] Collections for route', pathname, ':', Array.from(collections));
            return collections;
        },
        
        // Проверка доступа к роуту для текущего пользователя
        hasAccessToRoute(pathname, userRole) {
            const routesModel = self.schemas.routes;
            if (!routesModel || !routesModel.routes) return false;
            
            const routeConfig = self.findRouteConfig(pathname, routesModel.routes);
            if (!routeConfig) return false;
            
            // Проверяем явные разрешения
            if (routeConfig.access && routeConfig.access.roles) {
                return routeConfig.access.roles.includes(userRole);
            }
            
            // По умолчанию - запрещено (явные разрешения)
            return false;
        },
        
        // Получить доступные роуты для роли (для меню)
        getAccessibleRoutes(userRole) {
            const routesModel = self.schemas.routes;
            if (!routesModel || !routesModel.routes) return [];
            
            const accessible = [];
            self.collectAccessibleRoutes(routesModel.routes, userRole, accessible);
            return accessible;
        },
        
        // Вспомогательная функция для рекурсивного сбора роутов
        collectAccessibleRoutes(routes, userRole, result, basePath = '') {
            for (const [path, config] of Object.entries(routes)) {
                const fullPath = basePath + path;
                
                // Проверяем доступ
                if (config.access && config.access.roles && config.access.roles.includes(userRole)) {
                    result.push({
                        path: fullPath,
                        title: config.title,
                        icon: config.icon,
                        component: config.component
                    });
                }
                
                // Рекурсивно проверяем дочерние
                if (config.children) {
                    self.collectAccessibleRoutes(config.children, userRole, result, fullPath);
                }
            }
        }
    }))
    .actions(self => ({
        // Действия по управлению моделями
        afterCreate() {
            self.registerSocketHandlers();
        },

        // Регистрация локальной коллекции страницы
        registerPageCollection(name, collection) {
            console.log(`[MSTModelStore] Registering page collection: ${name}`);
            self.pageCollections.set(name, collection);

            // Возвращаем функцию отмены регистрации для cleanup
            return () => {
                console.log(`[MSTModelStore] Unregistering page collection: ${name}`);
                self.pageCollections.delete(name);
            };
        },

        // Универсальный метод получения коллекции (сначала локальная, потом глобальная)
        getCollection(name) {
            // Приоритет 1: локальная коллекция страницы
            if (self.pageCollections.has(name)) {
                console.log(`[MSTModelStore] Using page collection: ${name}`);
                return self.pageCollections.get(name);
            }

            // Приоритет 2: глобальная коллекция
            if (self.collectionManagers[name]) {
                console.log(`[MSTModelStore] Using global collection: ${name}`);
                return self.collectionManagers[name];
            }

            console.log(`[MSTModelStore] Collection not found: ${name}`);
            return null;
        },
        
        // Регистрация WebSocket обработчиков
        registerSocketHandlers() {
            const rootStore = getRoot(self);
            
            // Модели - это основа системы, подписка всегда активна
            rootStore.registerEventHandler('models:updated', (data) => {
                console.log('Model schema update received via WebSocket:', data);
                // Парсим key вида 'models:users' чтобы получить имя модели
                if (data.key && data.key.startsWith('models:')) {
                    const modelName = data.key.split(':')[1];
                    // Загружаем только измененную модель
                    self.loadSingleModel(modelName);
                }
            }, 'MSTModelStore');
            
            rootStore.registerEventHandler('models:deleted', (data) => {
                console.log('Model deleted via WebSocket:', data);
                if (data.modelName) {
                    self.deleteSchemaFromWebSocket(data.modelName);
                }
            }, 'MSTModelStore');
            
            rootStore.registerEventHandler('models:created', (data) => {
                console.log('Model created via WebSocket:', data);
                if (data.modelName && data.model) {
                    self.updateSchemaFromWebSocket(data.modelName, data.model);
                }
            }, 'MSTModelStore');
            
            console.log('[MSTModelStore] Models WebSocket events registered');
        },
        
        // Обновление схемы из WebSocket
        updateSchemaFromWebSocket(modelName, model) {
            // Обновляем схему
            self.schemas = {
                ...self.schemas,
                [modelName]: model
            };
            
            // Пересоздаем MST модель
            self.generateMSTModel(modelName, model);
            
            // Если обновилась routes модель или модель с коллекциями, пересоздаем менеджеры
            if (modelName === 'routes' || self.isCollectionModel(modelName)) {
                self.initializeCollectionManagers();
            }
        },
        
        // Проверка является ли модель коллекцией
        isCollectionModel(modelName) {
            // Проверяем есть ли эта модель в метаданных коллекций
            return !!(self.collectionsMetadata && self.collectionsMetadata[modelName]);
        },
        
        // Удаление схемы из WebSocket
        deleteSchemaFromWebSocket(modelName) {
            const newSchemas = { ...self.schemas };
            delete newSchemas[modelName];
            self.schemas = newSchemas;
            
            delete self.mstModels[modelName];
        },
        
        // Загрузка одной модели с сервера
        loadSingleModel: flow(function* (modelName) {
            try {
                console.log(`[MSTModelStore] Loading single model: ${modelName}`);
                const { apiClient } = getRoot(self);
                const response = yield apiClient.get(`/api/models/${modelName}`);
                
                if (response.data) {
                    // Обновляем только эту схему
                    self.schemas = {
                        ...self.schemas,
                        [modelName]: response.data
                    };
                    
                    // Перегенерируем только эту MST модель
                    self.generateMSTModel(modelName, response.data);
                    
                    console.log(`[MSTModelStore] Model ${modelName} updated from server`);
                    
                    // Обновляем схему во всех местах где она может использоваться
                    
                    // 1. Обновляем в collectionManagers если есть
                    const collectionManager = self.collectionManagers[modelName];
                    if (collectionManager) {
                        if (collectionManager.setPermissions) {
                            collectionManager.setPermissions({
                                modelSchema: response.data
                            });
                        } else if (collectionManager.modelSchema !== undefined) {
                            collectionManager.modelSchema = response.data;
                        }
                        console.log(`[MSTModelStore] Updated schema for collection manager: ${modelName}`);
                    }
                    
                    // 2. Обновляем в текущем pageStore если это та же коллекция
                    const currentPageStore = self.pageStores.get(self.currentRoute);
                    if (currentPageStore && currentPageStore.entityName === modelName) {
                        if (currentPageStore.setPermissions) {
                            currentPageStore.setPermissions({
                                modelSchema: response.data
                            });
                        } else if (currentPageStore.modelSchema !== undefined) {
                            currentPageStore.modelSchema = response.data;
                        }
                        console.log(`[MSTModelStore] Updated schema for current page store: ${modelName}`);
                    }
                    
                    // 3. Проверяем все pageStores и обновляем схему где нужно
                    self.pageStores.forEach((store, route) => {
                        if (store.entityName === modelName && store !== currentPageStore) {
                            if (store.setPermissions) {
                                store.setPermissions({
                                    modelSchema: response.data
                                });
                            } else if (store.modelSchema !== undefined) {
                                store.modelSchema = response.data;
                            }
                            console.log(`[MSTModelStore] Updated schema for cached page store at ${route}`);
                        }
                    });
                }
            } catch (error) {
                console.error(`Failed to load model ${modelName}:`, error);
            }
        }),
        
        // Загрузка всех моделей с сервера
        loadModels: flow(function* () {
            self.isLoading = true;
            self.error = null;
            
            try {
                const { apiClient } = getRoot(self);
                console.log('Loading models from /api/models/...');
                const response = yield apiClient.get('/api/models/');
                console.log('Models response:', response);
                
                self.schemas = response.data || {};
                console.log('Models loaded from server:', Object.keys(self.schemas));
                
                // Генерируем MST модели для каждой загруженной схемы
                for (const [modelName, schema] of Object.entries(self.schemas)) {
                    self.generateMSTModel(modelName, schema);
                }
                
                // Создаем CollectionManager для основных сущностей только если их еще нет
                if (Object.keys(self.collectionManagers).length === 0) {
                    self.initializeCollectionManagers();
                }
                
                // Если есть текущий роут, обрабатываем его подписки
                if (self.currentRoute) {
                    console.log('[MSTModelStore] Reprocessing route after models loaded:', self.currentRoute);
                    self.handleRouteChange(self.currentRoute);
                }
                
                return true;
            } catch (error) {
                self.error = error.message;
                console.error('Failed to load models:', error);
                return false;
            } finally {
                self.isLoading = false;
            }
        }),
        
        // Генерация MST модели из схемы
        generateMSTModel(modelName, schema) {
            try {
                // Для коллекций генерируем ItemModel и CollectionModel
                const models = createModelCollection(modelName, schema);
                
                // Сохраняем сгенерированные модели
                self.mstModels[modelName] = models;
                
                console.log(`Generated MST models for ${modelName}:`, models);
            } catch (error) {
                console.error(`Failed to generate MST model for ${modelName}:`, error);
            }
        },
        
        // Инициализация метаданных коллекций (без создания самих коллекций)
        initializeCollectionManagers() {
            // Получаем модель роутов
            const routesModel = self.schemas.routes;
            if (!routesModel || !routesModel.routes) {
                console.warn('[MSTModelStore] No routes defined in routes model');
                return;
            }
            
            // Собираем уникальные коллекции из всех роутов
            const collectionsToCreate = new Map();
            
            // Функция для извлечения имени коллекции из URI пути
            function getCollectionNameFromPath(path) {
                // Убираем параметры типа :id
                const cleanPath = path.replace(/\/:[^\/]+/g, '');
                // Берем последний сегмент
                const segments = cleanPath.split('/').filter(Boolean);
                const lastSegment = segments[segments.length - 1] || '';
                
                // Возвращаем последний сегмент или 'root' для корневого пути
                return lastSegment || 'root';
            }
            
            // Рекурсивный обход роутов для сбора коллекций
            function processRoute(path, config, parentPath = '') {
                const fullPath = parentPath + path;
                
                // Пропускаем служебные роуты
                if (path === '*') return;
                
                // Если есть api endpoint - это потенциальная коллекция
                if (config.api) {
                    const collectionName = getCollectionNameFromPath(fullPath);
                    const apiEndpoint = config.api;
                    
                    // Сохраняем информацию о коллекции
                    if (!collectionsToCreate.has(collectionName)) {
                        collectionsToCreate.set(collectionName, {
                            name: collectionName,
                            apiEndpoint: apiEndpoint,
                            path: fullPath
                        });
                    }
                }
                
                // Обрабатываем дочерние роуты
                if (config.children) {
                    Object.entries(config.children).forEach(([childPath, childConfig]) => {
                        processRoute(childPath, childConfig, fullPath);
                    });
                }
            }
            
            // Обходим все роуты
            Object.entries(routesModel.routes).forEach(([path, config]) => {
                processRoute(path, config);
            });
            
            // Сохраняем метаданные для ленивого создания коллекций
            collectionsToCreate.forEach((info, collectionName) => {
                self.collectionMetadata.set(collectionName, info);
            });
            
            // Добавляем специальную коллекцию models (всегда доступна)
            self.collectionMetadata.set('models', {
                name: 'models',
                apiEndpoint: '/api/models/',
                path: '/models'
            });
            
            console.log('[MSTModelStore] Collection metadata initialized:', Array.from(self.collectionMetadata.keys()));
        },
        
        // Ленивое создание коллекции при первом обращении
        getOrCreateCollectionManager(collectionName) {
            // Блокируем создание коллекции editor - она должна быть только локальной в UniversalEditor
            if (collectionName === 'editor') {
                console.warn('[MSTModelStore] Блокирована попытка создать коллекцию editor');
                return null;
            }

            // Если уже создана - возвращаем
            if (self.collectionManagers[collectionName]) {
                return self.collectionManagers[collectionName];
            }

            // Получаем метаданные
            const info = self.collectionMetadata?.get(collectionName);
            if (!info) {
                console.warn(`[MSTModelStore] No metadata for collection: ${collectionName}`);
                return null;
            }
            
            // Определяем модель для коллекции
            let schema = null;
            let modelPath = collectionName;
                
                // Ищем схему: сначала отдельная модель, потом в business
                if (self.schemas[collectionName]) {
                    schema = self.schemas[collectionName];
                } else if (self.schemas.business && self.schemas.business[collectionName]) {
                    schema = self.schemas.business[collectionName];
                    modelPath = `business.${collectionName}`;
                } else if (collectionName === 'system' && self.schemas.system) {
                    schema = self.schemas.system;
                } else if (collectionName === 'models') {
                    // Для models создаем простую схему
                    schema = {
                        type: 'object',
                        properties: {
                            id: { type: 'string' },
                            name: { type: 'string' },
                            title: { type: 'string' }
                        }
                    };
                }
                
                if (!schema) {
                    console.warn(`[MSTModelStore] No schema found for collection: ${collectionName}`);
                    return null;
                } else {
                    // Для обычных коллекций используем createModelCollection!
                    const { CollectionModel } = createModelCollection(collectionName, schema);
                    
                    // Создаем экземпляр типизированной коллекции с environment
                    const collectionInstance = CollectionModel.create({
                        entityName: collectionName,
                        entitySingular: collectionName.replace(/s$/, ''), // простое правило для singular
                        apiEndpoint: info.apiEndpoint,
                        items: [],
                        isLoading: false,
                        error: null
                    }, {
                        rootStore: getRoot(self),
                        mstModelStore: self
                    });
                    
                    // Используем типизированную коллекцию напрямую
                    self.collectionManagers[collectionName] = collectionInstance;
                    
                    // Регистрируем WebSocket обработчики для коллекции
                    // Соглашение: entity:action (например, system:updated, user:created)
                    const rootStore = getRoot(self);
                    
                    // Обработчик создания
                    rootStore.registerEventHandler(`${collectionName}:created`, (data) => {
                        console.log(`[MSTModelStore] ${collectionName}:created event received, reloading collection`, data);
                        // При создании перезагружаем коллекцию с сервера БЕЗ индикатора загрузки
                        if (collectionInstance.load) {
                            collectionInstance.load(true); // silent = true
                        }
                    }, `MSTModelStore-${collectionName}`);
                    
                    // Обработчик обновления
                    rootStore.registerEventHandler(`${collectionName}:updated`, (data) => {
                        console.log(`[MSTModelStore] ${collectionName}:updated event received, reloading collection`, data);
                        // При обновлении перезагружаем коллекцию с сервера БЕЗ индикатора загрузки
                        if (collectionInstance.load) {
                            collectionInstance.load(true); // silent = true
                        }
                    }, `MSTModelStore-${collectionName}`);
                    
                    // Обработчик удаления
                    rootStore.registerEventHandler(`${collectionName}:deleted`, (data) => {
                        console.log(`[MSTModelStore] ${collectionName}:deleted event received, reloading collection`, data);
                        // При удалении перезагружаем коллекцию с сервера БЕЗ индикатора загрузки
                        if (collectionInstance.load) {
                            collectionInstance.load(true); // silent = true
                        }
                    }, `MSTModelStore-${collectionName}`);
                    
                    console.log(`[MSTModelStore] WebSocket handlers registered for: ${collectionName}`);
                }
                
                console.log(`[MSTModelStore] Created collection manager for: ${collectionName}`);
            
            return self.collectionManagers[collectionName];
        },
        
        // Получить схему по пути (например "business.Project")
        getSchemaByPath(path) {
            if (!path) return null;
            
            const parts = path.split('.');
            let current = self.schemas;
            
            for (const part of parts) {
                if (!current || !current[part]) {
                    return null;
                }
                current = current[part];
            }
            
            return current;
        },
        
        // Обновить схему модели на сервере
        updateSchema: flow(function* (modelName, updatedSchema) {
            try {
                // Отправляем на сервер через API
                const { apiClient } = getRoot(self);
                yield apiClient.put(`/api/models/${modelName}`, updatedSchema);
                console.log(`Model schema ${modelName} sent to server`);
                // Обновление придёт через WebSocket
            } catch (error) {
                console.error(`Failed to save model schema ${modelName}:`, error);
                throw error;
            }
        }),
        
        // Обработка изменения роута
        handleRouteChange(pathname) {
            console.log('[MSTModelStore] Route changed to:', pathname);
            self.currentRoute = pathname;
            
            // Определяем какие коллекции нужны для текущего роута
            const requiredCollections = self.getCollectionsForRoute(pathname);
            const currentCollections = new Set(self.activeSubscriptions);
            
            // Отписываемся от ненужных
            for (const collectionName of currentCollections) {
                if (!requiredCollections.has(collectionName)) {
                    self.unsubscribeCollection(collectionName);
                }
            }
            
            // Подписываемся на новые
            for (const collectionName of requiredCollections) {
                if (!currentCollections.has(collectionName)) {
                    self.subscribeCollection(collectionName);
                }
            }
        },
        
        // Подписаться на коллекцию
        subscribeCollection(collectionName) {
            // Ленивое создание коллекции при первом обращении
            const manager = self.getOrCreateCollectionManager(collectionName);
            if (manager) {
                console.log('[MSTModelStore] Subscribing to', collectionName);
                self.activeSubscriptions.add(collectionName);
                
                // Автозагрузка данных если коллекция пуста
                if (manager.load) {
                    const items = manager.items;
                    if ((Array.isArray(items) && items.length === 0) || items === null) {
                        manager.load();
                    }
                }
            } else {
                console.warn(`[MSTModelStore] No collection manager for: ${collectionName}`);
            }
        },
        
        // Отписаться от коллекции
        unsubscribeCollection(collectionName) {
            const manager = self.collectionManagers[collectionName];
            if (manager) {
                console.log('[MSTModelStore] Unsubscribing from', collectionName);
                self.activeSubscriptions.delete(collectionName);
            } else {
                console.warn(`[MSTModelStore] No collection manager for: ${collectionName}`);
            }
        },
        
        // Создание стора страницы
        createPageStore(currentPath) {
            // Проверяем кеш
            if (self.pageStores.has(currentPath)) {
                const cached = self.pageStores.get(currentPath);
                console.log(`[MSTModelStore] Using cached page store for ${currentPath}`);
                return cached;
            }
            
            // Получаем конфигурацию роута
            const routeConfig = self.findRouteConfig(
                currentPath, 
                self.schemas.routes?.routes
            );
            
            if (!routeConfig) {
                console.warn(`No route config found for ${currentPath}`);
                return null;
            }
            
            // Получаем API endpoint
            const apiEndpoint = routeConfig.api || self.getApiEndpoint(currentPath);
            
            // Определяем имя модели из URI пути, а не из API endpoint
            // Для /settings/base -> base
            // Для /settings/users -> users
            const pathSegments = currentPath.split('/').filter(Boolean);
            let modelName = pathSegments[pathSegments.length - 1]; // последний сегмент

            // Проверка что это не UUID (для путей вида /projects/:id)
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (uuidPattern.test(modelName)) {
                console.log(`[MSTModelStore] Skipping UUID segment: ${modelName}`);
                // Для URL вида /projects/:id берем предыдущий сегмент (projects)
                if (pathSegments.length > 1) {
                    modelName = pathSegments[pathSegments.length - 2];
                } else {
                    console.log(`[MSTModelStore] No model name found for path: ${currentPath}`);
                    return null; // Нет модели для этого пути
                }
            }

            // Специальная обработка для editor страниц
            if (modelName === 'editor') {
                // Для /templates/:id/editor → берем "templates"
                if (pathSegments.includes('templates')) {
                    modelName = 'templates';
                    console.log(`[MSTModelStore] Editor page detected, using model: ${modelName}`);
                }
                // Можно добавить другие editor страницы если понадобятся
            }

            // Получаем схему модели по имени из URI
            let modelSchema = self.getSchemaByPath(modelName) || self.schemas[modelName];
            
            console.log(`[MSTModelStore] Looking for model: ${modelName}, found schema:`, !!modelSchema);
            console.log(`[MSTModelStore] Schema details:`, modelSchema?.title || modelSchema?.name || 'no title/name');
            
            // Если схема все еще не найдена, пробуем найти в составных моделях
            if (!modelSchema) {
                // Проверяем все модели на наличие вложенных схем
                for (const [topModelName, topModel] of Object.entries(self.schemas)) {
                    if (topModel && typeof topModel === 'object' && !topModel.$schema) {
                        // Ищем схемы только в объектах без $schema (составные модели)
                        for (const [nestedName, nestedSchema] of Object.entries(topModel)) {
                            if (nestedSchema && typeof nestedSchema === 'object' && 
                                !nestedName.startsWith('$') && 
                                !nestedName.startsWith('x-') &&
                                nestedName !== 'definitions') {
                                // Проверяем соответствие имени
                                if (nestedName.toLowerCase() === modelName.toLowerCase() ||
                                    nestedName.toLowerCase() === modelName.toLowerCase() + 'config') {
                                    modelSchema = nestedSchema;
                                    modelName = nestedName;
                                    break;
                                }
                            }
                        }
                        if (modelSchema) break;
                    }
                }
            }
            
            // Получаем доступные операции для текущей роли
            const allowedOperations = self.getAllowedOperations(routeConfig);
            
            // Если есть модель - возвращаем CollectionManager с установленными разрешениями
            if (modelName) {
                const collectionManager = self.getOrCreateCollectionManager(modelName);
                if (collectionManager) {
                    // Устанавливаем метаданные и конфигурацию роута
                    // Права теперь вычисляются реактивно в views
                    if (collectionManager.setPermissions) {
                        collectionManager.setPermissions({
                            modelSchema: modelSchema,
                            routeConfig: routeConfig
                        });
                    } else {
                        // Для специальных коллекций без MST (например, models)
                        collectionManager.permissions = {
                            canCreate: allowedOperations.includes('POST'),
                            canUpdate: allowedOperations.includes('PUT'),
                            canDelete: allowedOperations.includes('DELETE'),
                            canRead: allowedOperations.includes('GET'),
                            hasSpecialOperations: allowedOperations.includes('SPEC')
                        };
                        collectionManager.modelSchema = modelSchema;
                    }
                    
                    // Кешируем
                    self.pageStores.set(currentPath, collectionManager);
                    console.log(`[MSTModelStore] Using CollectionManager for ${currentPath}`);
                    
                    // Загружаем данные если еще не загружены
                    if (collectionManager.items && collectionManager.items.length === 0 && !collectionManager.isLoading) {
                        if (collectionManager.load) {
                            // Для коллекций используем frozen режим чтобы избежать ошибок с computed полями на списках
                            collectionManager.load({ frozen: true });
                        }
                    }
                    
                    return collectionManager;
                }
            }
            
            // Для страниц без данных возвращаем null
            console.log(`[MSTModelStore] No data model for ${currentPath}`);
            return null;
        },
        
        // Получает разрешенные операции для текущей роли
        getAllowedOperations(routeConfig) {
            const rootStore = getRoot(self);
            const userRole = rootStore.auth.userRole;
            const rolesConfig = self.schemas.routes?.roles;
            
            if (!rolesConfig || !rolesConfig[userRole]) {
                return ['GET']; // По умолчанию только чтение
            }
            
            const roleOperations = rolesConfig[userRole].operations || [];
            
            // Просто возвращаем операции роли
            // ownershipCheck и globalPermissions - это серверные проверки
            return roleOperations;
        },
        
        // Очистка кеша сторов страниц
        clearPageStoresCache() {
            self.pageStores.forEach(store => {
                if (store.cleanup) store.cleanup();
            });
            self.pageStores.clear();
            console.log('[MSTModelStore] Page stores cache cleared');
        },
        
        // Сброс состояния
        reset() {
            // Отписываемся от всех WebSocket событий коллекций
            for (const collectionName of self.activeSubscriptions) {
                self.unsubscribeCollection(collectionName);
            }
            
            for (const manager of Object.values(self.collectionManagers)) {
                if (manager.dispose) manager.dispose();
            }
            
            // Очищаем кеш сторов страниц
            self.clearPageStoresCache();
            
            self.schemas = {};
            self.mstModels = {};
            self.collectionManagers = {};
            self.activeSubscriptions.clear();
            self.currentRoute = null;
            self.isLoading = false;
            self.error = null;
        }
    }));

export { MSTModelStore };